import { useSuspenseQuery } from '@tanstack/react-query';
import type React from 'react';

import type {
    BlockOpenAPIOperationData,
    BlockOpenAPISchemasData,
    BlockOpenAPIWebhookData,
} from '@gitbook/doc-core';
import type { Filesystem } from '@gitbook/openapi-parser';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
} from '@gitbook/spine-react';

import { apiQuery, useAssertCurrentOrganizationId } from '../../../state';
import { checkIsValidFilesystem, OpenAPIWaitForSpec } from '../../OpenAPI';

/**
 * Fetch the schemas (will suspense).
 */
export function OpenAPIFetch(props: {
    data: BlockOpenAPIOperationData | BlockOpenAPISchemasData | BlockOpenAPIWebhookData;
    children: (props: {
        specUrl: string;
        filesystem: Filesystem;
        organizationId: string;
    }) => React.ReactNode;
}) {
    const {
        data: { ref: contentRef },
        children,
    } = props;

    const organizationId = useAssertCurrentOrganizationId();

    const specParams = { organizationId, specSlug: contentRef.spec };

    const { data: latestVersionContent } = useSuspenseQuery(
        apiQuery('getLatestOpenAPISpecVersionContent', specParams)
    );

    if (!latestVersionContent) {
        return <OpenAPIWaitForSpec organizationId={organizationId} specSlug={contentRef.spec} />;
    }

    const specUrl = latestVersionContent.url;
    const { filesystem } = latestVersionContent;

    if (!checkIsValidFilesystem(filesystem)) {
        return (
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.OpenAPI />
                </EmptyStateIcon>
                <Heading>Invalid OpenAPI specification</Heading>
                <Description>
                    This OpenAPI specification is invalid or not supported. Please replace it with a
                    valid OpenAPI spec.
                </Description>
                <EmptyStateActions>
                    <Button
                        label="Edit OpenAPI spec"
                        href={{
                            screen: {
                                type: 'openapi_spec',
                                organization: organizationId,
                                spec: contentRef.spec,
                            },
                        }}
                    />
                </EmptyStateActions>
            </EmptyState>
        );
    }

    return children({ specUrl, filesystem, organizationId });
}
